import {
  isTeleportalTrackId,
  TELEPORTAL_TRACK_VISUAL_BY_ID,
  type TeleportalTrackVisual,
} from '@tonnie/heroes';
/**
 * Types and constants for RelocationKitBuilder — matching Brain.Learning InstantiatedTask output
 */

import { Scale, Plane, Home, type LucideIcon } from '@tonnie/assets';

export interface KitTask {
  template_id: string;
  track: string;
  task_code: string;
  task_name_key: string;
  description_key: string;
  task_type: string;
  household_scope: string;
  ownership_mode: string;
  is_company_task: boolean;
  priority: string;
  is_blocking: boolean;
  is_required: boolean;
  requires_document: boolean;
  required_document_types: string[];
  estimated_duration: string;
  miles_reward: number;
  order_index: number;
  depends_on: string[];
  household_member_id: string | null;
  household_member_name: string | null;
  household_member_type: string | null;
  vault_linked: boolean;
  vault_document_id: string | null;
  location?: 'home_country' | 'target_country' | 'either' | 'online';
  document_metadata?: KitDocumentMeta[];
}

export interface KitDocumentMeta {
  document_type: string;
  location: 'home_country' | 'target_country' | 'either' | 'online';
  validity_days: number | null;
  requires_certification: boolean;
  certification_type: string | null;
  issuing_authority_key: string | null;
}

export interface KitTrack {
  track_code: string;
  track_name_key: string;
  is_foundation: boolean;
  order_index: number;
  icon: string;
  color: string;
}

export interface KitMilestone {
  template_id: string;
  track: string;
  title_key: string;
  order_index: number;
  tasks_required: number;
  required_task_codes: string[];
}

export interface RelocationKit {
  tracks: KitTrack[];
  tasks: KitTask[];
  milestones: KitMilestone[];
  dependency_graph: Array<{ from: string; to: string }>;
  critical_path: string[];
  complexity: {
    score: number;
    level: 'simple' | 'standard' | 'complex';
    factors: string[];
  };
  metadata: {
    origin: string;
    destination: string;
    total_tasks: number;
    total_documents_required: number;
    vault_linked_count: number;
    household_member_count: number;
    generated_at: string;
  };
}

export const TRACK_ICONS: Record<string, LucideIcon> = {
  legal: Scale,
  travel: Plane,
  setup: Home,
};

/**
 * A kit track's colours and name — the canonical TelePortal track visual (the
 * same the TelePortal page draws), or none for a code that is not a track. The
 * kit used to build `text-${color}-600` at runtime, a class the production
 * build could not see and so never generated.
 */
export function trackVisual(code: string): TeleportalTrackVisual | null {
  return isTeleportalTrackId(code) ? TELEPORTAL_TRACK_VISUAL_BY_ID[code] : null;
}

export const COMPLEXITY_BADGE: Record<string, { labelKey: string; variant: string }> = {
  simple: {
    labelKey: 'teleportal.kit.complexity.simple',
    variant: 'bg-tone-green-subtle text-tone-green',
  },
  standard: {
    labelKey: 'teleportal.kit.complexity.standard',
    variant: 'bg-tone-amber-subtle text-tone-amber',
  },
  complex: {
    labelKey: 'teleportal.kit.complexity.complex',
    variant: 'bg-tone-red-subtle text-tone-red',
  },
};

export const PRIORITY_DOT: Record<string, string> = {
  high: 'bg-tone-red-solid',
  medium: 'bg-tone-amber-solid',
  low: 'bg-tone-green-solid',
};
